import { useState, type ReactNode } from "react";
import { TriangleAlert } from "lucide-react";
import type { PayRateSource } from "@/entities/pay-rate";
import { Badge } from "@/shared/components/ui/badge";
import { Input } from "@/shared/components/ui/input";
import { RoleBadge } from "@/shared/components/ui/RoleBadge";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { formatCurrency } from "@/shared/lib/formatCurrency";
import { PAY_RATE_SOURCE_LABELS, RATE_INPUT_PATTERN } from "./shiftRates";

const TAG_TONES: Record<PayRateSource, "info" | "bad" | "neutral"> = {
  Custom: "info",
  Authorized: "neutral",
  SiteDefault: "neutral",
  Compensation: "neutral",
  None: "bad",
};

interface RateFieldProps {
  name: string;
  rate: number | null;
  onChange: (rate: number | null) => void;
}

function RateField({ name, rate, onChange }: RateFieldProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? (rate === null ? "" : rate.toFixed(2));

  return (
    <div className="flex items-center gap-1">
      <span className="text-sm text-muted-foreground">$</span>
      <Input
        inputMode="decimal"
        className="h-8 w-24 text-right tabular-nums"
        placeholder="0.00"
        aria-label={`Hourly pay rate for ${name}`}
        value={text}
        onFocus={(e) => {
          setDraft(text);
          e.currentTarget.select();
        }}
        onChange={(e) => {
          const next = e.target.value;
          if (!RATE_INPUT_PATTERN.test(next)) return;
          setDraft(next);
          onChange(next === "" || next === "." ? null : Number(next));
        }}
        onBlur={() => setDraft(null)}
      />
      <span className="text-xs text-muted-foreground">/h</span>
    </div>
  );
}

interface ShiftRateRowProps {
  name: string;
  employeeType?: string;
  rate: number | null;
  source: PayRateSource | null;
  loading?: boolean;
  readOnly: boolean;
  onRateChange: (rate: number | null) => void;
  action?: ReactNode;
  notices?: string[];
}

export function ShiftRateRow({
  name,
  employeeType,
  rate,
  source,
  loading = false,
  readOnly,
  onRateChange,
  action,
  notices = [],
}: ShiftRateRowProps) {
  const tagSource: PayRateSource | null = loading ? null : rate === null ? "None" : source;

  return (
    <div className="space-y-1.5 rounded-md border p-2.5">
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="truncate text-sm font-medium">{name}</span>
          <RoleBadge employeeType={employeeType} />
        </div>
        {loading ? (
          <Skeleton className="h-8 w-28" />
        ) : readOnly ? (
          <span className="text-sm font-medium tabular-nums">
            {rate === null ? "—" : `${formatCurrency(rate)} /h`}
          </span>
        ) : (
          <RateField name={name} rate={rate} onChange={onRateChange} />
        )}
        <div className="flex w-28 justify-end">
          {tagSource && (
            <Badge tone={TAG_TONES[tagSource]}>{PAY_RATE_SOURCE_LABELS[tagSource]}</Badge>
          )}
        </div>
      </div>
      {action}
      {notices.map((notice) => (
        <p
          key={notice}
          className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400"
        >
          <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" />
          {notice}
        </p>
      ))}
    </div>
  );
}
